import React, { useState, useEffect } from 'react';
import {
  Search, MapPin, Calendar, User, Image as ImageIcon,
  CheckCircle, XCircle, Clock, ExternalLink, Loader2,
  AlertCircle, Navigation, ThumbsUp, ThumbsDown,
  BadgeCheck, X, Eye, RefreshCw, AlertTriangle,
  Copy, Phone, Mail, FileText, Ban, ZoomIn
} from 'lucide-react';
import { GoogleMap, useJsApiLoader, MarkerF } from '@react-google-maps/api';
import { useBusinessSubmissions } from '../../authentication_components/BusinessSubmissionContext';

const PUERTO_RICO_BOUNDS = {
  north: 18.52,
  south: 17.88,
  west: -67.95,
  east: -65.22
};

const mapContainerStyle = {
  width: '100%',
  height: '256px',
  borderRadius: '0.75rem'
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  mapTypeId: 'roadmap',
  gestureHandling: 'cooperative',
  styles: [
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] }
  ],
  restriction: {
    latLngBounds: PUERTO_RICO_BOUNDS,
    strictBounds: true
  }
};

// =================================================================
// 0. Image Lightbox Component
// =================================================================
const ImageLightbox = ({ imageUrl, alt, onClose }) => {
  if (!imageUrl) return null;
  return (
    <div
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
      >
        <X className="h-6 w-6" />
      </button>
      <img
        src={imageUrl}
        alt={alt}
        referrerPolicy="no-referrer"
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

// =================================================================
// 0.5 Improved Image Component with Loading State
// =================================================================
const BusinessImage = ({
  imageUrl,
  alt,
  className = "",
  containerClassName = "",
  showZoom = false,
  onZoom = null
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
  }, [imageUrl]);

  if (!imageUrl) {
    return (
      <div className={`flex items-center justify-center bg-slate-200 ${containerClassName}`}>
        <div className="flex flex-col items-center text-slate-400">
          <ImageIcon className="h-8 w-8 mb-1" />
          <span className="text-xs">Sin imagen</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${containerClassName}`}>
      {/* Loading Spinner */}
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
          <Loader2 className="h-6 w-6 text-violet-500 animate-spin" />
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
          <div className="flex flex-col items-center text-slate-400">
            <AlertCircle className="h-8 w-8 mb-1" />
            <span className="text-xs">Error al cargar</span>
          </div>
        </div>
      )}

      {/* Actual Image */}
      <img
        src={imageUrl}
        alt={alt}
        referrerPolicy="no-referrer"
        className={`${className} ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
      />

      {/* Zoom Button Overlay */}
      {showZoom && !loading && !error && onZoom && (
        <button
          onClick={onZoom}
          className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-lg text-white transition-all opacity-0 group-hover:opacity-100"
          title="Ver imagen completa"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

// =================================================================
// 1. Action Dialog (Approve/Reject/Duplicate)
// =================================================================
const ActionDialog = ({ isOpen, onClose, type, onConfirm, loading }) => {
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const isApprove = type === 'approve';
  const isDuplicate = type === 'duplicate';

  const handleConfirm = () => {
    onConfirm(notes);
    setNotes('');
  };

  const getHeaderColor = () => {
    if (isApprove) return 'bg-emerald-50 border-emerald-100 text-emerald-800';
    if (isDuplicate) return 'bg-slate-50 border-slate-100 text-slate-800';
    return 'bg-red-50 border-red-100 text-red-800';
  };

  const getIcon = () => {
    if (isApprove) return <ThumbsUp className="h-6 w-6 text-emerald-600" />;
    if (isDuplicate) return <Copy className="h-6 w-6 text-slate-600" />;
    return <ThumbsDown className="h-6 w-6 text-red-600" />;
  };

  const getTitle = () => {
    if (isApprove) return 'Aprobar Sugerencia de Negocio';
    if (isDuplicate) return 'Marcar como Duplicado';
    return 'Rechazar Sugerencia';
  };

  const getDescription = () => {
    if (isApprove) return 'Se enviará un correo electrónico automático al negocio invitándolos a registrarse en PR LocaL. ¿Desea agregar una nota administrativa?';
    if (isDuplicate) return 'Esta sugerencia se marcará como duplicada. No se enviará correo al negocio, pero el estado se actualizará.';
    return 'Por favor, indique la razón del rechazo. Esta nota será guardada internamente.';
  };

  const getButtonClass = () => {
    if (isApprove) return 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200';
    if (isDuplicate) return 'bg-slate-600 hover:bg-slate-700 shadow-slate-200';
    return 'bg-red-600 hover:bg-red-700 shadow-red-200';
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <div className={`p-6 border-b ${getHeaderColor()}`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-white/50">
              {getIcon()}
            </div>
            <h3 className="text-lg font-bold">
              {getTitle()}
            </h3>
          </div>
        </div>
        <div className="p-6">
          <p className="text-slate-600 mb-4 text-sm">
            {getDescription()}
          </p>

          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Notas Administrativas {isApprove ? '(Opcional)' : '(Recomendado)'}
          </label>
          <textarea
            className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm"
            rows={3}
            placeholder={isApprove ? "Ej. Información validada..." : "Ej. Negocio ya registrado, datos incorrectos..."}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`flex-1 px-4 py-2.5 text-white font-medium rounded-xl shadow-md transition-colors flex items-center justify-center disabled:opacity-50 ${getButtonClass()}`}
            >
              {loading ? (
                <Loader2 className="animate-spin h-5 w-5" />
              ) : (
                'Confirmar'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 2. Details Drawer / Modal
// =================================================================
const SubmissionDetailModal = ({ submission, onClose, onAction }) => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places']
  });

  if (!submission) return null;

  const openInGoogleMaps = () => {
    if (submission.business_coordinate_lat && submission.business_coordinate_lng) {
      window.open(`https://www.google.com/maps?q=${submission.business_coordinate_lat},${submission.business_coordinate_lng}`, '_blank');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-PR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Prepare map center
  const mapCenter = submission.business_coordinate_lat && submission.business_coordinate_lng
    ? { lat: Number(submission.business_coordinate_lat), lng: Number(submission.business_coordinate_lng) }
    : { lat: 18.2208, lng: -66.5901 };

  // Styles based on status
  const getStatusStyle = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'approved') return 'bg-emerald-50 border-emerald-100 text-emerald-800';
    if (s === 'rejected') return 'bg-red-50 border-red-100 text-red-800';
    if (s === 'duplicate') return 'bg-slate-100 border-slate-200 text-slate-800';
    return 'bg-amber-50 border-amber-100 text-amber-800';
  };

  const getStatusIcon = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'approved') return <CheckCircle className="h-5 w-5" />;
    if (s === 'rejected') return <XCircle className="h-5 w-5" />;
    if (s === 'duplicate') return <Copy className="h-5 w-5" />;
    return <Clock className="h-5 w-5" />;
  };

  const getStatusText = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'approved') return 'Aprobado';
    if (s === 'rejected') return 'Rechazado';
    if (s === 'duplicate') return 'Duplicado';
    return 'Pendiente';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 flex items-center justify-center p-4 md:p-8" onClick={onClose}>
        <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row" onClick={e => e.stopPropagation()}>
          {/* Left Col: Image & Map */}
          <div className="md:w-1/2 bg-slate-100 p-6 overflow-y-auto border-r border-slate-200">
            <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-violet-500" /> Referencia Visual
            </h4>

            {/* Main Image with Improved Component */}
            <div className="group rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white mb-6">
              <BusinessImage
                imageUrl={submission.business_image_url}
                alt={submission.business_name}
                className="w-full h-64 object-cover cursor-pointer"
                containerClassName="w-full h-64"
                showZoom={!!submission.business_image_url}
                onZoom={() => setLightboxImage(submission.business_image_url)}
              />
            </div>

            {/* Map Section */}
            <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
              <Navigation className="h-5 w-5 text-violet-500" /> Ubicación del Negocio
            </h4>

            {loadError ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-red-600">Error al cargar el mapa</p>
              </div>
            ) : !isLoaded ? (
              <div className="bg-slate-200 rounded-xl h-64 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-violet-500 animate-spin" />
              </div>
            ) : submission.business_coordinate_lat && submission.business_coordinate_lng ? (
              <div className="space-y-3">
                <div className="border-2 border-slate-300 rounded-xl overflow-hidden shadow-md">
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={15}
                    options={mapOptions}
                  >
                    <MarkerF position={mapCenter} />
                  </GoogleMap>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <p className="font-mono text-sm text-slate-600 font-semibold">
                    {Number(submission.business_coordinate_lat).toFixed(6)}, {Number(submission.business_coordinate_lng).toFixed(6)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{submission.business_city}</p>
                </div>

                <button
                  onClick={openInGoogleMaps}
                  className="w-full px-4 py-2 bg-white border border-violet-200 rounded-lg text-sm font-bold text-violet-600 hover:bg-violet-50 transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  <ExternalLink className="h-4 w-4" /> Abrir en Google Maps
                </button>
              </div>
            ) : (
              <div className="bg-slate-200 rounded-xl h-64 flex flex-col items-center justify-center relative overflow-hidden border border-slate-300">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#475569_1px,transparent_1px)] [background-size:16px_16px]"></div>
                <MapPin className="h-10 w-10 text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">Sin coordenadas disponibles</p>
              </div>
            )}
          </div>

          {/* Right Col: Details & Actions */}
          <div className="md:w-1/2 flex flex-col h-full">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-start bg-white">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-violet-500 bg-violet-50 px-2 py-0.5 rounded-full uppercase tracking-wide">
                    {submission.business_category || 'Sin categoría'}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">
                    ID: {submission.suggestion_id}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-slate-800">{submission.business_name}</h2>
                <div className="flex items-center text-slate-500 text-sm mt-1">
                  <MapPin className="h-3.5 w-3.5 mr-1" /> {submission.business_city || 'Sin ciudad'}
                </div>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-6">

              {/* Prevent Further Suggestions Indicator */}
              {submission.prevent_further_suggestions && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-3">
                  <Ban className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-red-800">Sugerencias Bloqueadas</p>
                    <p className="text-xs text-red-600">Este negocio está marcado para no recibir más sugerencias.</p>
                  </div>
                </div>
              )}

              {/* Status Display */}
              <div className={`p-4 rounded-xl border ${getStatusStyle(submission.submission_status)}`}>
                <h5 className="font-bold flex items-center gap-2">
                  {getStatusIcon(submission.submission_status)}
                  Estado: {getStatusText(submission.submission_status)}
                </h5>
                {submission.admin_notes && (
                  <div className="mt-2 pt-2 border-t border-black/10 text-sm opacity-90">
                    <span className="font-semibold text-xs uppercase opacity-70">Nota Admin:</span> {submission.admin_notes}
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <h5 className="text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-1">
                    <Phone className="h-3 w-3" /> Teléfono
                  </h5>
                  <p className="text-sm text-slate-700 font-medium">
                    {submission.business_phone || 'No disponible'}
                  </p>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <h5 className="text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-1">
                    <Mail className="h-3 w-3" /> Email del Negocio
                  </h5>
                  <p className="text-sm text-slate-700 font-medium truncate" title={submission.business_email}>
                    {submission.business_email || 'No disponible'}
                  </p>
                </div>
              </div>

              {/* Description */}
              {submission.business_description && (
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <h5 className="text-xs font-bold text-violet-600 uppercase mb-2 flex items-center gap-1">
                    <FileText className="h-3 w-3" /> Descripción
                  </h5>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {submission.business_description}
                  </p>
                </div>
              )}

              {/* Suggester Info */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <h5 className="text-xs font-bold text-slate-400 uppercase mb-3">Sugerido Por</h5>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-600">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-700">
                        {(submission.first_name || submission.last_name)
                          ? `${submission.first_name || ''} ${submission.last_name || ''}`.trim()
                          : 'Usuario Anónimo'}
                      </span>
                      {submission.user_id && (
                        <span className="text-blue-500" title="Usuario Registrado">
                          <BadgeCheck className="h-4 w-4" />
                        </span>
                      )}
                    </div>
                    {submission.user_id && (
                      <p className="text-xs text-slate-500 font-mono">User ID: {submission.user_id}</p>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-200 flex gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {formatDate(submission.suggestion_date)}
                  </span>
                  {submission.suggestion_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {submission.suggestion_time}
                    </span>
                  )}
                </div>
              </div>

            </div>

            {/* Footer Actions */}
            {(submission.submission_status || '').toLowerCase() === 'pending' && (
              <div className="p-6 border-t border-slate-100 bg-slate-50 grid grid-cols-3 gap-3">
                <button
                  onClick={() => onAction('reject', submission)}
                  className="py-3 px-2 bg-white border border-red-200 text-red-600 rounded-xl font-bold hover:bg-red-50 transition-colors flex flex-col items-center justify-center gap-1 text-sm shadow-sm"
                >
                  <XCircle className="h-5 w-5" /> Rechazar
                </button>
                <button
                  onClick={() => onAction('duplicate', submission)}
                  className="py-3 px-2 bg-white border border-slate-300 text-slate-600 rounded-xl font-bold hover:bg-slate-100 transition-colors flex flex-col items-center justify-center gap-1 text-sm shadow-sm"
                >
                  <Copy className="h-5 w-5" /> Duplicado
                </button>
                <button
                  onClick={() => onAction('approve', submission)}
                  className="py-3 px-2 bg-violet-600 text-white rounded-xl font-bold hover:bg-violet-700 shadow-lg shadow-violet-200 transition-colors flex flex-col items-center justify-center gap-1 text-sm"
                >
                  <CheckCircle className="h-5 w-5" /> Aprobar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <ImageLightbox
          imageUrl={lightboxImage}
          alt={submission.business_name}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </>
  );
};

// =================================================================
// 3. Error Alert
// =================================================================
const ErrorAlert = ({ message, onDismiss }) => {
  if (!message) return null;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-bold text-red-800 text-sm">Error</h4>
          <p className="text-red-600 text-sm mt-1">{message}</p>
        </div>
        <button onClick={onDismiss} className="text-red-400 hover:text-red-600">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// =================================================================
// 4. Success Toast
// =================================================================
const SuccessToast = ({ message, onDismiss }) => {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => onDismiss(), 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onDismiss]);

  if (!message) return null;
  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3 shadow-lg">
        <CheckCircle className="h-5 w-5 text-emerald-600" />
        <p className="text-emerald-800 font-medium text-sm">{message}</p>
        <button onClick={onDismiss} className="text-emerald-400 hover:text-emerald-600">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// =================================================================
// 5. MAIN COMPONENT
// =================================================================
export default function BusinessSubmissions() {
  const {
    loading: contextLoading,
    error: contextError,
    getSuggestions,
    getPendingSuggestions,
    getApprovedSuggestions,
    getRejectedSuggestions,
    getDuplicateSuggestions,
    approveSuggestion,
    rejectSuggestion,
    markSuggestionAsDuplicate,
    clearError,
  } = useBusinessSubmissions();

  // State
  const [submissions, setSubmissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [actionDialog, setActionDialog] = useState({ isOpen: false, type: null, submission: null });
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState('');

  // Initial Fetch & Filter Change
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      setLocalError('');
      let result;
      const statusKey = filterStatus.toLowerCase();

      switch (statusKey) {
        case 'pending':
          result = await getPendingSuggestions({ limit: 100 });
          break;
        case 'approved':
          result = await getApprovedSuggestions({ limit: 100 });
          break;
        case 'rejected':
          result = await getRejectedSuggestions({ limit: 100 });
          break;
        case 'duplicate':
          result = await getDuplicateSuggestions({ limit: 100 });
          break;
        case 'all':
          result = await getSuggestions({ limit: 100 });
          break;
        default:
          result = await getSuggestions({ limit: 100 });
      }

      setSubmissions(result.suggestions || []);
    } catch (err) {
      console.error('[BusinessSubmissions] Fetch error:', err);
      setLocalError(err.message || 'Error al cargar sugerencias');
    }
  };

  // Local filtering by search
  const filteredSubmissions = submissions.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.business_name?.toLowerCase().includes(searchLower)) ||
      (item.business_city?.toLowerCase().includes(searchLower)) ||
      (item.suggestion_id?.toLowerCase().includes(searchLower)) ||
      (item.business_email?.toLowerCase().includes(searchLower))
    );
  });

  // Action Handlers
  const handleOpenAction = (type, submission) => {
    setActionDialog({ isOpen: true, type, submission });
  };

  const handleConfirmAction = async (notes) => {
    const { type, submission } = actionDialog;
    if (!submission) return;
    setProcessing(true);
    setLocalError('');

    try {
      if (type === 'approve') {
        await approveSuggestion(submission.suggestion_id, notes);
        setSuccessMessage(`Negocio "${submission.business_name}" aprobado y notificado.`);
      } else if (type === 'duplicate') {
        await markSuggestionAsDuplicate(submission.suggestion_id, notes);
        setSuccessMessage(`Sugerencia marcada como duplicada.`);
      } else {
        await rejectSuggestion(submission.suggestion_id, notes);
        setSuccessMessage(`Sugerencia rechazada.`);
      }

      await fetchData();
      setActionDialog({ isOpen: false, type: null, submission: null });
      setSelectedSubmission(null);
    } catch (err) {
      console.error('Error processing action:', err);
      setLocalError(err.message || 'Error al procesar la acción');
    } finally {
      setProcessing(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const s = (status || '').toLowerCase();
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-100',
      approved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      rejected: 'bg-red-50 text-red-700 border-red-100',
      duplicate: 'bg-slate-50 text-slate-700 border-slate-100'
    };
    const icons = {
      pending: Clock,
      approved: CheckCircle,
      rejected: XCircle,
      duplicate: Copy
    };
    const Icon = icons[s] || Clock;
    const text = s === 'pending' ? 'Pendiente' :
      s === 'approved' ? 'Aprobado' :
        s === 'rejected' ? 'Rechazado' :
          s === 'duplicate' ? 'Duplicado' : s;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[s] || styles.pending}`}>
        <Icon className="h-3 w-3 mr-1" />
        {text}
      </span>
    );
  };

  const displayError = localError || contextError;

  return (
    <div className="min-h-screen bg-slate-50/50">
      <SuccessToast message={successMessage} onDismiss={() => setSuccessMessage('')} />
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Sugerencias de Negocios</h1>
              <p className="text-sm text-slate-500">Administración de solicitudes de registro de negocios.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchData}
                disabled={contextLoading}
                className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
                title="Actualizar"
              >
                <RefreshCw className={`h-5 w-5 ${contextLoading ? 'animate-spin' : ''}`} />
              </button>
              <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg overflow-x-auto">
                {['Pending', 'Approved', 'Rejected', 'Duplicate', 'All'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status.toLowerCase())}
                    disabled={contextLoading}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${filterStatus === status.toLowerCase()
                        ? 'bg-white text-violet-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                      }`}
                  >
                    {status === 'All' ? 'Todos' :
                      status === 'Pending' ? 'Pendientes' :
                        status === 'Approved' ? 'Aprobados' :
                          status === 'Rejected' ? 'Rechazados' :
                            status === 'Duplicate' ? 'Duplicados' : status}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por negocio, ciudad, email o ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>
        </div>
      </div>

      <ErrorAlert message={displayError} onDismiss={() => { clearError(); setLocalError(''); }} />

      {/* Table Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden min-h-[400px]">
          {contextLoading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-violet-500 animate-spin mb-2" />
              <p className="text-slate-400 text-sm">Cargando sugerencias...</p>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <div className="bg-slate-50 p-3 rounded-full mb-3">
                <FileText className="h-6 w-6 text-slate-300" />
              </div>
              <p className="text-slate-500 font-medium">
                {searchTerm ? 'No se encontraron resultados.' : 'No hay sugerencias en esta categoría.'}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-2 text-sm text-violet-600 hover:text-violet-700"
                >
                  Limpiar búsqueda
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Negocio</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Categoría</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Sugerido Por</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Fecha</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Estado</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredSubmissions.map((item) => (
                    <tr
                      key={item.suggestion_id}
                      onClick={() => setSelectedSubmission(item)}
                      className="group hover:bg-violet-50/50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {/* Improved Thumbnail Image */}
                          <BusinessImage
                            imageUrl={item.business_image_url}
                            alt={item.business_name}
                            className="h-full w-full object-cover"
                            containerClassName="h-10 w-10 rounded-lg border border-slate-300 overflow-hidden flex-shrink-0"
                          />
                          <div>
                            <div className="font-bold text-slate-800 flex items-center gap-2">
                              {item.business_name}
                              {item.prevent_further_suggestions && (
                                <Ban className="h-3 w-3 text-red-500" title="Sugerencias Bloqueadas" />
                              )}
                            </div>
                            <div className="text-xs text-slate-500 flex items-center gap-1">
                              <MapPin className="h-3 w-3" /> {item.business_city || 'Sin ciudad'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-700 bg-slate-100 px-2 py-1 rounded-md font-medium">
                          {item.business_category || 'General'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-700">
                          {(item.first_name || item.last_name)
                            ? `${item.first_name || ''} ${item.last_name || ''}`.trim()
                            : 'Anónimo'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {item.suggestion_date ? new Date(item.suggestion_date).toLocaleDateString('es-PR') : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={item.submission_status} />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            className="p-2 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-100 transition-colors"
                            title="Ver Detalles"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSubmission(item);
                            }}
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          {(item.submission_status || '').toLowerCase() === 'pending' && (
                            <button
                              className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                              title="Aprobar"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAction('approve', item);
                              }}
                            >
                              <CheckCircle className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!contextLoading && filteredSubmissions.length > 0 && (
          <div className="mt-4 text-center text-sm text-slate-500">
            Mostrando {filteredSubmissions.length} {filteredSubmissions.length === 1 ? 'resultado' : 'resultados'}
            {searchTerm && ` para "${searchTerm}"`}
          </div>
        )}
      </div>

      {/* Modals */}
      <SubmissionDetailModal
        submission={selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
        onAction={handleOpenAction}
      />

      <ActionDialog
        isOpen={actionDialog.isOpen}
        type={actionDialog.type}
        loading={processing}
        onClose={() => setActionDialog({ isOpen: false, type: null, submission: null })}
        onConfirm={handleConfirmAction}
      />
    </div>
  );
}